import { useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { ClassifyResult } from './ClassifyResult';
import { SubscriptionStatus } from './SubscriptionStatus';
import { getPresignedUrl, uploadToS3, classifyDocument } from '../services/auth';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// SVG Icons
const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const SparklesIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z" />
  </svg>
);

const ImageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const FileIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const ChartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 20V10" />
    <path d="M12 20V4" />
    <path d="M6 20v-6" />
  </svg>
);

export function ClassifyPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [showSubscription, setShowSubscription] = useState(false);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
    if (!validTypes.includes(selectedFile.type)) {
      setError('Please select a valid image or PDF (JPEG, PNG, WebP, GIF or PDF)');
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('File must not exceed 10MB');
      return;
    }

    setFile(selectedFile);
    setError(null);
    setResult(null);

    if (selectedFile.type === 'application/pdf') {
      try {
        const previewUrl = await pdfToPreview(selectedFile);
        setPreview(previewUrl);
      } catch (err) {
        console.error('Error generating PDF preview:', err);
        setPreview(null);
      }
    } else {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    setResult(null);
    setUploadProgress(null);
  };

  const handleProcess = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);
    setUploadProgress('Getting URL...');

    try {
      let fileToUpload = file;
      let contentType = file.type;

      if (file.type === 'application/pdf') {
        setUploadProgress('Converting PDF...');
        const jpegBlob = await pdfToJpegBlob(file);
        fileToUpload = new File([jpegBlob], file.name.replace('.pdf', '.jpg'), { type: 'image/jpeg' });
        contentType = 'image/jpeg';
      }

      setUploadProgress('Getting URL...');
      const { uploadUrl, s3Key } = await getPresignedUrl(contentType);

      setUploadProgress('Uploading...');
      await uploadToS3(uploadUrl, fileToUpload, contentType);

      setUploadProgress('Classifying...');
      const response = await classifyDocument(s3Key);

      setResult(response.data || response);
      setUploadProgress(null);
    } catch (err) {
      console.error('Error classifying document:', err);
      setError(err.message || 'Error classifying document');
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const pdfToJpegBlob = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);
    const scale = 2;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: context, viewport }).promise;
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
  };

  const pdfToPreview = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);
    const scale = 1.5;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: context, viewport }).promise;
    return canvas.toDataURL('image/jpeg', 0.8);
  };

  return (
    <>
      <div className="hero">
        <div className="powered-badge">
          <SparklesIcon />
          <span>Powered by AI</span>
        </div>
        <h1>AI Document Classifier<br /><span>with AI</span></h1>
        <p>Upload your document and classify it automatically</p>

        <button
          className="btn-subscription-toggle"
          onClick={() => setShowSubscription(!showSubscription)}
        >
          <ChartIcon />
          {showSubscription ? 'Hide Status' : 'View Subscription Status'}
        </button>
      </div>

      {showSubscription && (
        <div className="subscription-panel">
          <SubscriptionStatus />
        </div>
      )}

      <div className="invoice-page">
        <div className="upload-section">
          <div className="section-header">
            <div className="step-number">1</div>
            <span className="section-title">Upload Document</span>
          </div>

          <div className="card">
            {!file ? (
              <div
                className="dropzone"
                onClick={() => document.getElementById('file-input').click()}
              >
                <input
                  id="file-input"
                  type="file"
                  className="file-input"
                  accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                  onChange={handleFileChange}
                />
                <div className="dropzone-icon">
                  <div className="dropzone-icon-glow"></div>
                  <div className="dropzone-icon-inner">
                    <UploadIcon />
                  </div>
                </div>
                <h3>Drag your document here</h3>
                <p>or click to browse files</p>
                <div className="file-types">
                  <span className="file-badge">
                    <ImageIcon />
                    PNG, JPG
                  </span>
                  <span className="file-badge">
                    <FileIcon />
                    PDF
                  </span>
                </div>
              </div>
            ) : (
              <div className="preview-wrapper">
                <img src={preview} alt="Preview" className="preview-image" />
                <div className="preview-footer">
                  <button type="button" className="btn-change-file" onClick={handleReset}>
                    <RefreshIcon />
                    Change image
                  </button>
                </div>
              </div>
            )}

            <button
              className="btn-process"
              onClick={handleProcess}
              disabled={!file || loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  {uploadProgress || 'Processing...'}
                </>
              ) : (
                'Classify Document'
              )}
            </button>

            {error && <div className="error-message">{error}</div>}
          </div>
        </div>

        <div className="result-section">
          <div className="section-header">
            <div className="step-number">2</div>
            <span className="section-title">Classification</span>
          </div>

          <div className="card">
            {result ? (
              <>
                <ClassifyResult data={result} />
                <button className="btn-new-invoice" onClick={handleReset}>
                  <PlusIcon />
                  New Document
                </button>
              </>
            ) : (
              <div className="result-placeholder">
                <div className="result-placeholder-icon">
                  <SparklesIcon />
                </div>
                <h3>Upload a document to classify it</h3>
                <p>PDF, JPG or PNG</p>
              </div>
            )}
          </div>

          <div className="section-header">
            <div className="step-number">3</div>
            <div className="ready-badge">
              Ready to load into your system
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
